/* eslint-disable max-lines -- GraphQL fragments file */
export const ACCOUNT_COUNTERS = `
  followCounter {
    follow
    followers
  }
  postCounter {
    total
    facebook
    twitter
    instagram
    marble
  }
`;

export const AVATAR = `
  _id
  url
  mimetype
`;

export const SHAREABLE_SETTINGS = `
  _id
  updatedAt
  accountId
  accountType
  firstName
  lastName
  fullName
  username
  avatar {
    ${AVATAR}
  }
  social {
    type
    username
    link
  }
  isFollowing
`;

export const BACKGROUND_SETTINGS = `
  colorMode
  backgroundImage
  headerImageUrl
  avatarStrokeType
  avatarAlignmentType
  markup {
    tabs {
      type
      name
      visible
      order
      extra {
        authId
        source
      }
    }
  }
  advancedColor {
    background
    secondary
    text
    icons
    line
    shadow1
    shadow2
    shadow3
    isActive
    primary
  }
  slideshowHidden
  brandingHidden
`;

export const ACCOUNT_LINKS = `
  name
  address
  imageUrl
  pinned
  order
  imageHidden
`;

export const MARBLE_LINK = `
  _id
  createdAt
  updatedAt
  title
  description
  url
  imageUrl
  type
  pinned
  order
  imageHidden
  postId
  source
  authId
  post {
    source
    attachments {
      media {
        description
        durationMs
        encoding
        height
        mimetype
        type
        url
        width
      }
      thumbnail {
        description
        durationMs
        encoding
        height
        mimetype
        type
        url
        width
      }
    }
  }
`;

export const LINK_PREVIEW = `
  _id
  createdAt
  updatedAt
  url
  title
  description
  mediaType
  image 
`;

export const POST = `
  _id
  authId
  authInnerId
  innerId
  source
  postUrl
  type
  collaborationType
  sourceStatus
  message
  hashtags
  mentions
  keywords
  postUrlCta
  attachmentsProcessed
  attachments {
    media {
      description
      durationMs
      encoding
      height
      mimetype
      type
      url
      width
    }
    thumbnail {
      description
      durationMs
      encoding
      height
      mimetype
      type
      url
      width
    }
  }
  engagement {
    likes
    comments
    views
    shares
    total
  }
  customEngagement {
    likes
    comments
    views
    shares
  }
  createdAt
  updatedAt
`;

export const AUTH_NOTE = `
  _id
  createdAt
  updatedAt
  authId
  message
  emails
  documentUrls
  address
  campaignAuthNotes {
    message
  }
`;

export const ORGANIZATION_PAYMENT_DETAILS = `
  _id
  createdAt
  updatedAt
  paymentProvider
  payoutAccountInnerId
  onboardingLink
  isReadyToReceivePayments
  dashboardLink
  bankName
  last4
`;

export const CAMPAIGN_AUTH_TASK_LINK = `
  _id
  createdAt
  updatedAt
  campaignId
  authId
  shortUrl
  longUrl
  isCustomLink
  name
  source
`;

export const CAMPAIGN_AUTH_TASK = `
  _id
  createdAt
  updatedAt
  authId
  campaignId
  postCount
  storyCount
  videoCount
  deadline
  status
  secret
  isClaimed
  description
  paymentOptions {
    flatFee
    commission
    gift
    terms
  }
  organizationPaymentDetails {
    ${ORGANIZATION_PAYMENT_DETAILS}
  }
  links {
    ${CAMPAIGN_AUTH_TASK_LINK}
  }
  campaign {
    _id
    createdAt
    updatedAt
    name
    avatar {
      url
    }
    mentions
    hashtags
    keywords
    websiteUrl
    startDate
    endDate
    description
    budget
  }
`;

/**
 * Analytics without `postBucket` — everything a list screen reads.
 *
 * `postBucket` is one row per post per creator. On a 18-creator saved list it
 * was 537 KB of the 594 KB response and about 1.5 s of the ~2.6 s the query
 * took; without it the same query is 58 KB / ~1.1 s. Nothing outside the
 * campaign analytics charts reads it, so list screens should use this and let
 * `AUTH_ANALYTIC` stay whole for the screens that plot per-post series.
 */
export const AUTH_ANALYTIC_LIST = `
  _id
  createdAt
  updatedAt
  authInnerId
  authSource
  followersCount
  followersCount30DAgo
  followerGrowthRate30D
  postsCount
  postsThisWeek
  videoCount
  storiesCount
  averageVideoViews
  avgViewsPerPost
  viewsEstimated
  averageComments
  averageShares
  averageEngagementRate
  averageLikes
  postingFrequency
  organicEngagementRate
  sponsoredEngagementRate
  sponsoredPostRate
  contentPerformance {
    type
    engagementRate
    count
  }
  engagement {
    likes
    comments
    shares
    views
  }
  engagementMentioned {
    likes
    comments
    shares
    views
  }
  postHashtags
  postKeywords
  createdAtFormatted
  lastPostCreatedAt
`;

/** Full analytics: the list fragment plus the per-post series. */
export const AUTH_ANALYTIC = `
  ${AUTH_ANALYTIC_LIST}
  postBucket {
    innerId
    type
    engagement {
      likes
      comments
      shares
      views
    }
  }
`;

export const CAMPAIGN_AUTH_ANALYTICS = `
  authInnerId
  engagement
  followersCount
  storiesCount
  postsCount
  postsCountLatest48h
  videoCount
  viewsCount
  likesCount
  commentsCount
  sharesCount
  latestPosts {
    ${POST}
  }
  createdAt
`;

export const CAMPAIGN_AUTH_ANALYTICS_FLAT = `
  likes
  comments
  shares
  views
  engagement
  engagementRate
  totalFollowers
  postsCount
  storiesCount
  videoCount
`;

/**
 * The creator selection, parameterised by which analytics block to embed, so
 * `AUTH` and `AUTH_LIST` cannot drift apart: they differ in exactly one thing,
 * whether `postBucket` comes along.
 */
const authFields = (analytics: string): string => `
  _id
  createdAt
  updatedAt
  innerId
  valid
  username
  avatar {
    url
  }
  profileUrl
  expiredDate
  source
  status
  description
  summary
  authAnalyticsLatest {
    ${analytics}
  }
  followersCount
  note {
    ${AUTH_NOTE}
  }
  creatorStatus {
    _id
    createdAt
    updatedAt
    authId
    status
  }
  groups {
    _id
    name
  }
  isPrivate
  contactPhoneNumber
  publicEmail
  fullName
  cityName
  country
  brandSafetyScore
  brandSafetyStatus
  brandSafetyFlags
`;

export const AUTH = authFields(AUTH_ANALYTIC);

/** `AUTH` minus the per-post series — see `AUTH_ANALYTIC_LIST`. */
export const AUTH_LIST = authFields(AUTH_ANALYTIC_LIST);

export const AUTH_CAMPAIGNS = `
  _id
  name
  avatar {
    url
  }
  startDate
  endDate
  finishDate
`;

export const CAMPAIGN_AUTH = `
  ${AUTH}
  campaignTasks(campaignId: $campaignId) {
    ${CAMPAIGN_AUTH_TASK}
  }
  campaignAuthAnalyticsLatest(campaignId: $campaignId) {
    ${CAMPAIGN_AUTH_ANALYTICS_FLAT}
  }
  persona {
    _id
    displayName
    avatarUrl
    anomalyFlags
    totalFollowers
  }
`;

// ============================================
// LIGHT FRAGMENTS - Optimized for campaignAuth
// Only includes fields that are actually used
// ============================================

// Light version of AUTH_NOTE - only message is used
export const AUTH_NOTE_LIGHT = `
  message
`;

// Light version of ORGANIZATION_PAYMENT_DETAILS - only display fields
export const ORGANIZATION_PAYMENT_DETAILS_LIGHT = `
  onboardingLink
  isReadyToReceivePayments
  dashboardLink
  bankName
  last4
`;

// Light version of CAMPAIGN_AUTH_TASK_LINK - all fields are used
export const CAMPAIGN_AUTH_TASK_LINK_LIGHT = `
  _id
  shortUrl
  longUrl
  isCustomLink
  name
  clicks
  uniqueClicks
  lastClickAt
  status
  code
  source
  healthStatus
  resolvedUtm {
    source
    medium
    campaign
    content
    term
  }
`;

// Light version of AUTH_ANALYTIC - only used analytics fields
export const AUTH_ANALYTIC_LIGHT = `
  followersCount
  postsCount
  videoCount
  storiesCount
  averageEngagementRate
  averageLikes
  averageComments
  engagement {
    likes
    comments
    shares
    views
  }
`;

// Light version of CAMPAIGN_AUTH_TASK - removes unused timestamps and campaign fields
export const CAMPAIGN_AUTH_TASK_LIGHT = `
  _id
  postCount
  storyCount
  videoCount
  deadline
  status
  secret
  isClaimed
  description
  paymentOptions {
    flatFee
    commission
    gift
    terms
  }
  organizationPaymentDetails {
    ${ORGANIZATION_PAYMENT_DETAILS_LIGHT}
  }
  links {
    ${CAMPAIGN_AUTH_TASK_LINK_LIGHT}
  }
  gift {
    id
    status
    note
    failureReason
    addressSubmittedAt
    orderedAt
    fulfilledAt
    lineItems {
      productId
      variantId
      quantity
      title
      variant
    }
    tracking {
      number
      url
      carrier
    }
  }
`;

// Light version of AUTH - removes ~14 unused fields
export const AUTH_LIGHT = `
  _id
  innerId
  username
  avatar {
    url
  }
  profileUrl
  isPrivate
  source
  followersCount
  publicEmail
  contactPhoneNumber
  cityName
  country
  creatorStatus {
    status
  }
  authAnalyticsLatest {
    ${AUTH_ANALYTIC_LIGHT}
  }
  note {
    ${AUTH_NOTE_LIGHT}
  }
`;

// Light version of CAMPAIGN_AUTH - combines all light fragments
export const CAMPAIGN_AUTH_LIGHT = `
  ${AUTH_LIGHT}
  campaignTasks(campaignId: $campaignId) {
    ${CAMPAIGN_AUTH_TASK_LIGHT}
  }
  campaignAuthAnalyticsLatest(campaignId: $campaignId) {
    ${CAMPAIGN_AUTH_ANALYTICS_FLAT}
  }
  outreachStatus(campaignId: $campaignId) {
    status
  }
  persona {
    _id: id
    displayName
    avatarUrl
    anomalyFlags
    totalFollowers
  }
`;

export const PAYMENT = `
  _id
  type
  status
  paidUntilDate
`;

export const EmailAuth = `
  passed
`;

export const PERSONAL_INFO = `
  type
  name
  website
  description
  address
  email
  phoneNumber
  worldRank
  homeCourse
  handicap
  holes
  par
  courseLength
  ghin
`;

export const CONTACT_DETAILS = `
  targetEmail
  infoMessage
  buttonText
  enabled
`;

export const ACCOUNT_SHAREABLE = `
  _id
  createdAt
  updatedAt
  accountId
  accountType
  firstName
  lastName
  fullName
  username
  social {
    type
    username
    link
  }
  avatar {
    _id
    createdAt
    updatedAt
    mimetype
    url
    moderationResult {
      Confidence
      Name
      ParentName
      Timestamp
    }
  }
  isFollowing
`;

export const MARBLE_SUBSCRIPTION = `
  _id
  createdAt
  updatedAt
  provider
  customerInnerId
  productInnerId
  innerId
  status
  startDate
  endDate
  priceInnerId
  lastPaymentDate
  paidUntilDate
`;

export const PROFILE = `
  _id
  createdAt
  updatedAt
  name
  avatar {
    url
  }
  onboardingPassed
`;

export const ORGANIZATION_PROFILE = `
  _id
  createdAt
  updatedAt
  organizationId
  name
  description
  website
  avatar {
    url
  }
  industry
  goals
  extraInfo
  size
  email
  phone
  address
  country
  timezone
`;

export const ORGANIZATION_ROLE = `
  _id
  createdAt
  updatedAt
  organizationId
  name
  description
  roleType
  isDefault
  isSystem
  permissions {
    key
    enabled
    editDisabled
    title
    campaignId
  }
  color
  icon
  priority
`;

export const ORGANIZATION_MEMBER = `
  _id
  createdAt
  updatedAt
  organizationId
  accountId
  roleType
  status
  permissions {
    key
    enabled
    editDisabled
    title
    campaignId
  }
  invitedByAccountId
  invitedByEmail
  invitedAt
  joinedAt
  title
  department
  notes
  isPrimary
  lastActivityAt
  profile {
    name
    avatar {
      url
    }
  }
`;

export const ORGANIZATION = `
  _id
  createdAt
  updatedAt
  accountId
  type
  isActive
  isPublic
  membersCount
  token
  members {
    ${ORGANIZATION_MEMBER}
  }
  profile {
    ${ORGANIZATION_PROFILE}
  }
`;

// Slim org fragment for auth responses (signIn/verifyEmail/signInGoogle):
// also omits `members`, which the backend forbids without an authenticated
// session (none exists yet during sign-in). `stripeCustomerId` is already
// absent from the base fragment — it's billing-only and per-org guarded, so
// it's fetched on demand by the Billing screen's own query.
export const ORGANIZATION_AUTH = `
  _id
  createdAt
  updatedAt
  accountId
  type
  isActive
  isPublic
  membersCount
  token
  profile {
    ${ORGANIZATION_PROFILE}
  }
`;

// Full account fragment WITH the org `members` collection. `members` is
// auth-guarded — only request it in an org-scoped token context. The initial
// account load (GetAccountData) and sign-in use ACCOUNT_V2_AUTH instead; keep
// this only for callers that already hold org context and genuinely need
// members.
export const ACCOUNT_V2 = `
  _id
  createdAt
  updatedAt
  loginType
  isVerified
  email
  phoneNumber
  profile {
    ${PROFILE}
  }
  organizations {
    ${ORGANIZATION}
  }
`;

// Slim org fragment (no `members`) used by both the sign-in response and the
// initial account load (GetAccountData), so neither tree hits the auth-guarded
// `members` field before an org-scoped token context exists.
export const ACCOUNT_V2_AUTH = `
  _id
  createdAt
  updatedAt
  loginType
  isVerified
  email
  phoneNumber
  profile {
    ${PROFILE}
  }
  organizations {
    ${ORGANIZATION_AUTH}
  }
`;

export const ACCOUNT = `
  _id
  email
  username
  searchName
  phoneNumber
  gender
  tagline
  birthDate
  firstName
  lastName
  onboardingPassed
  type
  marbleId
  tooltips {
    type
  }
  shareableSettings {
    ${SHAREABLE_SETTINGS}
  }
  backgroundSettings {
    ${BACKGROUND_SETTINGS}
  }
  emailAuth  {
    ${EmailAuth}
  }
  auths {
    ${AUTH}
  }
  links {
    ${MARBLE_LINK}
  }
  gaLinks {
    _id
    createdAt
    updatedAt
    name
    address
  }
  previewImageUrl
  affilatedProgramParticipant
  paymentInfo {
    paypalEmail
    stripeAccountId
  }
  meta {
    ab_flags {
      key
      value
    }
  }
  dataCollectionSettings {
    zapierLastFetch
    email
    phoneNumber
  }
  personaInfo {
    ${PERSONAL_INFO}
  }
  contactDetails {
    ${CONTACT_DETAILS}
  }
  ambassadorEnabled
  haveManagedAccountRelations
  gaConnection {
    _id
    createdAt
    updatedAt
    gaAccountId
    gaAccountName
    gaPropertyId
    gaPropertyName
  }
  gaConnectionList {
    _id
    linkId
    createdAt
    updatedAt
    gaAccountId
    gaAccountName
    gaPropertyId
    gaPropertyName
  }
  subscriptions {
    ${MARBLE_SUBSCRIPTION}
  }
`;

export const FEED_POST = `
  paginatedResults {
    ${POST}
  }
`;

export const ANALYTIC_FIELD = `
  date
  total
  sources {
    source
    amount
  }
`;

export const PROFILE_SCORE = `
  current
  max
`;

export const SHOPIFY_PRODUCT = `
  _id
  createdAt
  updatedAt
  authInnerId
  innerId
  title
  description
  handle
  productType
  price {
    amount
    currencyCode
  }
  collections {
    id
    title
    handle
  }
  productUrl
  images {
    id
    url
    width
    height
    altText
  }
`;

export const GA_STAT = `
  date
  total
  sources {
    source
    amount
  }
`;

export const CAMPAIGN_PAYOUT = `
  _id
  createdAt
  updatedAt
  authId
  paymentId
  provider
  currency
  amount
  status
  transactionInnerId
`;

export const CAMPAIGN_PAYMENT = `
  _id
  createdAt
  updatedAt
  campaignId
  authItems {
    authId
    amount
    currency
  }
  provider
  currency
  amount
  status
  fee {
    initialAmount
    marbleFee
    stripeInvoiceFee
    stripePayoutFee
    totalAmount
    totalFee
  }
  invoice {
    _id
    createdAt
    updatedAt
    organizationId
    campaignId
    campaignPaymentId
    provider
    customerId
    innerId
    status
    statusMessage
    amount
    currency
    paymentUrl
  }
  payouts {
    ${CAMPAIGN_PAYOUT}
  }
`;

export const GOOGLE_ANALYTICS_CONNECTION = `
  _id
  createdAt
  updatedAt
  organizationId
  name
  websiteUrl
  gaAccountId
  gaAccountName
  gaPropertyId
  gaPropertyName
  gaWebStreamId
  gaWebStreamName
  measurementId
  connected
  status
  isDefault
  lastSyncAt
  trackedEvents {
    _id
    name
    pagePathFilter
    pagePathOperator
  }
`;

export const GA_ACCOUNT = `
  _id
  email
  defaultPropertyId
  status
`;

/* Shopify integration (Track A + B). Money fields are minor currency
 * units (cents); percent discounts use basis points. See
 * `docs/frontend/shopify-integration.md`. */
export const SHOPIFY_STORE = `
  id
  myshopifyDomain
  displayName
  planDisplay
  planShopifyPlus
  currency
  status
  isPrimary
  connectedAt
  scopes
`;

export const SHOPIFY_DISCOUNT_SUMMARY = `
  shopifyDiscountId
  code
  status
  redemptionCount
`;

/* Brand-side Marble mirror of a Shopify product (Tracks A + B). Disambiguated
 * from the legacy creator-side `SHOPIFY_PRODUCT` fragment above, which targets
 * a different domain object (creator's connected shop, with `_id` / variants
 * / collections). */
export const SHOPIFY_STORE_PRODUCT = `
  id
  shopifyStoreId
  shopifyProductId
  title
  handle
  status
  imageUrl
  priceMin
  priceMax
  currency
  updatedAtShopify
  createdAt
  updatedAt
`;

// Live variant picker for the gifting create flow (Track E).
// shopifyVariantId is the gid to pass as a gift line item's variantId.
export const SHOPIFY_PRODUCT_VARIANT = `
  shopifyVariantId
  title
  price
  availableForSale
`;

export const CAMPAIGN_PROMO_CODE = `
  id
  campaignId
  authId
  auth {
    username
    avatar {
      url
    }
  }
  shopifyStoreId
  code
  origin
  discountType
  discountValue
  appliesTo
  startsAt
  endsAt
  maxUses
  onePerCustomer
  status
  redemptionCount
  revenueAttributed
  createdAt
  updatedAt
`;

export const CAMPAIGN_OFFLINE_REVENUE = `
  _id
  createdAt
  updatedAt
  campaignId
  authId
  amount
`;

export const CAMPAIGN_ANALYTICS = `
  comments
  cpc
  cpe
  cpo
  cpv
  emv
  engagement
  engagementRate
  gaClicks
  gaPageviews
  gaPurchases
  gaRevenue
  gaSessions
  likes
  linkClicks
  postsCount
  roi
  shares
  spent
  storiesCount
  totalFollowers
  updatedAt
  videoCount
  views
`;

export const CAMPAIGN = `
  _id
  createdAt
  updatedAt
  organizationId
  name
  primaryObjective
  targetAudience
  targetReach
  productType
  campaignType
  budget
  totalExpectedDeliverables
  description
  avatar {
    s3Key
    url
  }
  influencerAuthIds
  mentions
  hashtags
  keywords
  sweepEnabled
  startDate
  finishDate
  endDate
  gaConnectionId
  excludedPostIds
  includedPostIds
  gaReportItems {
    authId
    metricName
    metricValue
  }
  status
  utmTemplateId
  storeId
  defaultRedeemLimit
  defaultExpiryDays
  analyticsSummary {
    ${CAMPAIGN_ANALYTICS}
  }
  payouts {
    ${CAMPAIGN_PAYOUT}
  }
  payments {
    ${CAMPAIGN_PAYMENT}
  }
  promoCodes {
    id
    code
    authId
    status
    redemptionCount
    revenueAttributed
  }
  offlineRevenue {
    ${CAMPAIGN_OFFLINE_REVENUE}
  }
  gaConnection {
    ${GOOGLE_ANALYTICS_CONNECTION}
  }
`;

// Lightweight fragment for campaign list (useBrandCampaigns)
export const CAMPAIGN_LIST_ITEM = `
  _id
  name
  avatar {
    url
  }
  influencerAuthIds
  productType
  budget
  startDate
  finishDate
  endDate
  hashtags
  mentions
  keywords
  status
  totalExpectedDeliverables
  analyticsSummary {
    ${CAMPAIGN_ANALYTICS}
  }
`;

// Minimal fragment for campaign selection (useAddToCampaignButton)
export const CAMPAIGN_MINIMAL = `
  _id
  name
  influencerAuthIds
`;

export const AUTH_FROM_GROUP = `
  _id
  createdAt
  updatedAt
  innerId
  valid
  username
  avatar {
    url
  }
  profileUrl
  expiredDate
  source
  status
  description
  summary
  followersCount
  groups {
    _id
    name
  }
`;

export const AUTH_GROUP = `
  _id
  name
  authIds
  authCount
  auths @skip(if: $skipAuths) {
    ${AUTH_FROM_GROUP}
  }
  createdAt
  updatedAt
`;

export const AI_SEARCH_HISTORY_CANDIDATE = `
  _id
  historyId
  originStageId
  authInnerId
  username
  source
  from
  latestDecision {
    type
    violation
  }
  latestScore
  createdAt
  auth {
    ${AUTH}
  }
  decisions {
    llmReasons
  }
`;

export const AI_SEARCH_HISTORY_STAGE = `
  currentCriteria {
    hard {
      followersMin
      followersMax
      engagementRateMin
      platforms
      excludePrivate
      excludeLowInfo
    }
    soft {
      prompt
      negativePrompt
      negativeKeywords
      geoHint
      nicheKeywords
    }
    amount
  }
  summary
  totalAccepted
  totalRejected
`;

export const AI_SEARCH_HISTORY = `
  _id
  organizationId
  initialPrompt
  searchState {
    ${AI_SEARCH_HISTORY_STAGE}
  }
  status
  budget {
    maxStages
    usedStages
    maxSeconds
    usedSeconds
  }
  acceptedIndexed
  acceptedTotal
  createdAt
  updatedAt
  title
`;

export const BACKGROUND_ADVANCED_COLOR = `
  background
  secondary
  text
  icons
  line
  shadow1
  shadow2
  shadow3
  primary
  isActive
`;

export const LINK_BIO_BACKGROUND_SETTINGS = `
  createdAt
  updatedAt
  linkbioId
  organizationId
  colorMode
  backgroundImage
  headerImageUrl
  avatarStrokeType
  avatarAlignmentType
  advancedColor {
    ${BACKGROUND_ADVANCED_COLOR}
  }
  brandingHidden
`;

export const LINK_BIO_LINK = `
  _id
  createdAt
  updatedAt
  order
  linkbioId
  organizationId
  title
  description
  url
  pinned
  imageHidden
  postId
  authId
  media {
    url
  }
  source
`;

export const LINK_BIO_LINK_ICON = `
  _id
  createdAt
  updatedAt
  order
  linkbioId
  organizationId
  url
  visible
  source
  authId
`;

export const LINK_BIO_TAB = `
  _id
  createdAt
  updatedAt
  order
  linkbioId
  organizationId
  authId
  type
  name
  visible
  source
`;

export const LINK_BIO_PAGE = `
  _id
  createdAt
  updatedAt
  avatar {
    url
  }
  username
  gender
  tagline
  birthDate
  fullName
  website
  description
  address
  email
  phoneNumber
  previewImageUrl
  customDomain
  subdomain
  contactDetails {
    targetEmail
    infoMessage
    enabled
    buttonText
  }
  backgroundSettings {
    ${LINK_BIO_BACKGROUND_SETTINGS}
  }
  links {
    ${LINK_BIO_LINK}
  }
  linkIcons {
    ${LINK_BIO_LINK_ICON}
  }
  tabs {
    ${LINK_BIO_TAB}
  }
`;

export const AI_CHAT_MESSAGE = `
  _id
  senderId
  role
  blocks {
    type
    message
    entityKey
    title
    description
    entityId
    redirectUrl
    question
    options
    actionType
    entityName
    fields {
      label
      value
    }
    filename
    downloadUrl
    rowCount
    skippedUnindexed
    auth {
      _id
      username
      fullName
      source
      status
      valid
      followersCount
      postCount
      profileUrl
      avatar {
        url
      }
    }
    campaign {
      _id
      name
      description
      status
      authCount
      avatar {
        url
      }
    }
    campaignAuthTask {
      _id
      campaignId
      authId
      description
      status
      deadline
      postCount
    }
    authGroup {
      _id
      name
      authCount
    }
    aiSearchHistory {
      _id
      title
      initialPrompt
      status
      acceptedProgress {
        indexed
        total
      }
    }
    authNote {
      _id
      authId
      message
    }
    post {
      _id
      message
      source
      sourceStatus
      postUrl
      engagement {
        views
        total
      }
      attachments {
        thumbnail {
          url
        }
      }
    }
    gaConnection {
      _id
      name
      gaPropertyName
      websiteUrl
      status
      connected
    }
  }
  toolCallsUsed {
    name
    label
    input
    output
    success
    durationMs
    inputTruncated
    outputTruncated
  }
  createdAt
  senderProfile {
    _id
    name
    avatar {
      url
    }
  }
`;

export const AI_CHAT_SESSION = `
  _id
  title
  status
  kind
  systemActor
  executionMode
  errorMessage
  createdAt
  updatedAt
  turnCount
  rotationSuggested
`;

export const ORGANIZATION_SHIPPING_DETAILS = `
  _id
  createdAt
  updatedAt
  country
  email
  state
  city
  postalCode
  streetAddress
  fullName
`;

export const AUTH_GA_METRICS = `
  _id
  createdAt
  updatedAt
  campaignId
  gaConnectionId
  authId
  clicks
  pageviews
  revenue
  purchases
`;

export const SUB_MSG_AI_SEARCH_STAGE_DATA = `
  stage { ${AI_SEARCH_HISTORY_STAGE}
  }
`;

export const AI_SEARCH_STAGE_STATUS = `
  historyId
  stageId
  status
  message
  stats {
    accepted
    rejected
    dbVectorProcessed
    rerankProcessed
    googleProcessed
    durationMs
  }
`;

export const AI_SEARCH_STAGE = `
  _id
  historyId
  userPrompt
  relaxationLevel
  status
  stats {
    rerankProcessed
    dbVectorProcessed
    googleProcessed
    accepted
    rejected
    durationMs
    error
  }
  createdAt
  updatedAt
`;

export const AI_SEARCH_CANDIDATE_UPDATE = `
  _id
  authInnerId
  username
  source
  score
  factReasons
  llmReasons
  auth {
    ${AUTH}
  }
`;

export const NOTIFICATION = `
  id
  accountId
  actionUrl
  actorAccountId
  actorKind
  body
  createdAt
  ctaLabel
  notificationType
  organizationId
  readAt
  severity
  sourceEntityId
  sourceEntityType
  title
`;
